import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useQuickFillFeatureDetails } from '@frontend/api-quick-fill';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { AnimatedBadge } from '@frontend/components';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import {
  useScheduleActivityAccess,
  useAvailabilityFinderAccess,
  useScheduleRequestFeaturesAccess,
} from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Truncated } from '@frontend/design-system';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as scheduleFeatureDefinition } from './feature-definitions';

export const useScheduleNavItem = () => {
  const { t } = useTranslation('primaryNav');

  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { hasScheduleRequestFeaturesAccess } = useScheduleRequestFeaturesAccess();
  const { hasQuickFillFeature } = useQuickFillFeatureDetails();
  const { canViewBridgelineScheduleSettings } = useAiScheduleAccess();
  const { isFeatureHiddenInAllLocation, isLoadingWeaveVerifyFeature } =
    useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const { isScheduleActivityEnabled } = useScheduleActivityAccess();
  const { isAvailabilityFinderEnabled } = useAvailabilityFinderAccess();
  const hasAccessToEligibilityList = !isLoadingWeaveVerifyFeature && !isFeatureHiddenInAllLocation;

  const scheduleItems = scheduleFeatureDefinition.items;

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          ...scheduleItems['/calendar'],
          label: t('Calendar'),
          relativePath: '/calendar',
        },
        {
          ...scheduleItems['/availability'],
          label: t('Availability Finder'),
          relativePath: '/availability',
          trackingId: 'nav-schedule-availability',
          description: t('Preview the schedule for a selected service.'),
          show: canViewBridgelineScheduleSettings || isAvailabilityFinderEnabled,
        },
        {
          ...scheduleItems['/eligibility-list'],
          label: (
            <div css={insuranceEligibilityLabelStyles}>
              <Truncated>{t('Insurance Eligibility')}</Truncated>
              <AnimatedBadge label={t('New')} />
            </div>
          ),
          relativePath: '/eligibility-list',
          show: hasAccessToEligibilityList,
        },
        {
          ...scheduleItems['/schedule-activity'],
          label: t('Schedule Activity'),
          relativePath: '/schedule-activity',
          show: isScheduleActivityEnabled,
        },
        {
          ...scheduleItems['/request-history'],
          label: t('Request History'),
          relativePath: '/request-history',
          show: hasScheduleRequestFeaturesAccess && !isScheduleActivityEnabled,
        },
        {
          ...scheduleItems['/quickfill-history'],
          label: t('Waitlist History'),
          relativePath: '/quickfill-history',
          show: hasQuickFillFeature,
        },
        {
          ...scheduleItems['/debug'],
          label: t('Debug'),
          relativePath: '/debug',
          show: isDebugModeOn,
        },
      ]),
    [
      t,
      hasScheduleRequestFeaturesAccess,
      isScheduleActivityEnabled,
      hasQuickFillFeature,
      isDebugModeOn,
      canViewBridgelineScheduleSettings,
      hasAccessToEligibilityList,
      isAvailabilityFinderEnabled,
      scheduleItems,
    ]
  );

  return useMemo(
    () =>
      createNavItem('schedule', {
        iconName: 'calendar',
        label: t('Schedule'),
        root: '/schedule',
        subNavItems,
        settings: { path: '/schedule', label: '' },
      }),
    [t, subNavItems]
  );
};
const insuranceEligibilityLabelStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;
